import { create } from "zustand";
import type { FlashcardDeck, Flashcard, FlashcardStudyStats } from "./types";
import type { ReviewRating } from "./sm2";
import {
  fetchDecksForUser,
  fetchCardsForDeck,
  getDueCards,
  reviewCard,
  generateFlashcardsWithAI,
  computeStudyStats,
  addCardToDeck,
  deleteCard,
  toggleCardStar,
  updateCardFields,
  deleteDeck,
  updateDeckTitle,
} from "./api";
import { supabase } from "@/integrations/supabase/client";

type ViewMode = "list" | "deck";
type DeckTab = "spaced" | "fast";

type FlashcardsState = {
  userId: string | null;
  contentId: string | null;
  chatId: string | null;

  decks: FlashcardDeck[];
  selectedDeckId: string | null;
  cards: Flashcard[];

  viewMode: ViewMode;
  deckTab: DeckTab;

  isLoadingDecks: boolean;
  isLoadingCards: boolean;
  isGenerating: boolean;

  stats: FlashcardStudyStats;

  // Study mode state
  isStudyMode: boolean;
  dueCards: Flashcard[];
  studyIndex: number;

  // actions
  setContext: (ctx: { userId: string; contentId?: string | null; chatId?: string | null }) => void;
  loadDecks: () => Promise<void>;
  selectDeck: (deckId: string | null) => Promise<void>;
  setDeckTab: (tab: DeckTab) => void;
  setViewMode: (mode: ViewMode) => void;

  generateDeckFromAI: (opts: {
    topic?: string | null;
    sourceText?: string | null;
    numCards?: number;
  }) => Promise<void>;

  addEmptyCard: () => Promise<void>;
  deleteCardById: (cardId: string) => Promise<void>;
  toggleStarById: (cardId: string) => Promise<void>;
  updateCardById: (
    cardId: string,
    fields: Partial<Pick<Flashcard, "term" | "definition" | "hint" | "explanation" | "source">>
  ) => Promise<void>;
  deleteDeckById: (deckId: string) => Promise<void>;
  updateDeckTitleById: (deckId: string, title: string) => Promise<void>;

  // Study mode actions
  startStudyMode: () => Promise<void>;
  exitStudyMode: () => void;
  rateCard: (rating: ReviewRating) => Promise<void>;
  nextCard: () => void;
  previousCard: () => void;

  // Realtime subscription
  realtimeChannel: ReturnType<typeof supabase.channel> | null;
  subscribeToRealtimeUpdates: (deckId: string) => void;
  unsubscribeFromRealtime: () => void;
};

export const useFlashcardsStore = create<FlashcardsState>((set, get) => ({
  userId: null,
  contentId: null,
  chatId: null,

  decks: [],
  selectedDeckId: null,
  cards: [],

  viewMode: "list",
  deckTab: "spaced",

  isLoadingDecks: false,
  isLoadingCards: false,
  isGenerating: false,

  stats: { notStudied: 0, toReview: 0, studiedToday: 0 },

  // Study mode state
  isStudyMode: false,
  dueCards: [],
  studyIndex: 0,

  // Realtime subscription
  realtimeChannel: null,

  setContext: ({ userId, contentId = null, chatId = null }) =>
    set({ userId, contentId, chatId }),

  loadDecks: async () => {
    const { userId, contentId, chatId } = get();
    if (!userId) return;

    set({ isLoadingDecks: true });
    try {
      const decks = await fetchDecksForUser(userId, contentId, chatId);
      set({ decks });
    } finally {
      set({ isLoadingDecks: false });
    }
  },

  selectDeck: async (deckId) => {
    set({ selectedDeckId: deckId });

    if (!deckId) {
      set({ viewMode: "list", cards: [], stats: { notStudied: 0, toReview: 0, studiedToday: 0 } });
      return;
    }

    set({ viewMode: "deck", isLoadingCards: true });

    try {
      const cards = await fetchCardsForDeck(deckId);
      set({ cards, stats: computeStudyStats(cards) });
    } finally {
      set({ isLoadingCards: false });
    }
  },

  setDeckTab: (tab) => set({ deckTab: tab }),
  setViewMode: (mode) => set({ viewMode: mode }),

  generateDeckFromAI: async ({ topic, sourceText, numCards = 10 }) => {
    const { userId, contentId, chatId } = get();
    if (!userId) return;

    set({ isGenerating: true });
    try {
      const { deck, cards } = await generateFlashcardsWithAI({
        userId,
        contentId,
        chatId,
        topic,
        sourceText,
        numCards,
      });

      // refresh deck list & select new deck
      const decks = await fetchDecksForUser(userId, contentId, chatId);
      set({
        decks,
        selectedDeckId: deck.id,
        viewMode: "deck",
        cards,
        stats: computeStudyStats(cards),
      });
    } finally {
      set({ isGenerating: false });
    }
  },

  addEmptyCard: async () => {
    const { selectedDeckId, userId } = get();
    if (!selectedDeckId || !userId) return;
    await addCardToDeck(selectedDeckId, userId);
    const cards = await fetchCardsForDeck(selectedDeckId);
    set({ cards, stats: computeStudyStats(cards) });
  },

  deleteCardById: async (cardId) => {
    const { selectedDeckId } = get();
    if (!selectedDeckId) return;
    await deleteCard(cardId);
    const cards = await fetchCardsForDeck(selectedDeckId);
    set({ cards, stats: computeStudyStats(cards) });
  },

  toggleStarById: async (cardId) => {
    const card = get().cards.find((c) => c.id === cardId);
    if (!card) return;
    await toggleCardStar(cardId, card.is_starred);
    const cards = get().cards.map((c) =>
      c.id === cardId ? { ...c, is_starred: !c.is_starred } : c
    );
    set({ cards });
  },

  updateCardById: async (cardId, fields) => {
    await updateCardFields(cardId, fields);
    const cards = get().cards.map((c) =>
      c.id === cardId ? { ...c, ...fields } : c
    );
    set({ cards });
  },

  deleteDeckById: async (deckId) => {
    const { userId, contentId, chatId } = get();
    if (!userId) return;
    await deleteDeck(deckId);
    const decks = await fetchDecksForUser(userId, contentId, chatId);
    set({ decks, selectedDeckId: null, viewMode: "list", cards: [] });
  },

  updateDeckTitleById: async (deckId, title) => {
    await updateDeckTitle(deckId, title);
    const decks = get().decks.map((d) =>
      d.id === deckId ? { ...d, title } : d
    );
    set({ decks });
  },

  // Study mode actions
  startStudyMode: async () => {
    const { selectedDeckId, subscribeToRealtimeUpdates } = get();
    if (!selectedDeckId) return;

    const dueCards = await getDueCards(selectedDeckId);
    
    // Subscribe to realtime updates for this deck
    subscribeToRealtimeUpdates(selectedDeckId);
    
    set({
      isStudyMode: true,
      dueCards,
      studyIndex: 0,
    });
  },

  exitStudyMode: () => {
    const { selectedDeckId, userId, dueCards, unsubscribeFromRealtime } = get();
    
    // Unsubscribe from realtime updates
    unsubscribeFromRealtime();
    
    // Record study session before clearing state
    const remainingCount = dueCards.length;
    // We need to know the original count - store it when starting study mode
    // For now, use the dueCards that were removed during the session
    // The original count was set when startStudyMode ran; cards get removed from dueCards as they're rated
    
    if (selectedDeckId && userId) {
      // Calculate studied count: cards were removed from dueCards as they were rated
      // We can estimate from the deck's current state
      const studiedInSession = get().cards.filter((c) => {
        const updatedAt = new Date(c.updated_at);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        return updatedAt >= fiveMinutesAgo && c.updated_at !== c.created_at;
      }).length;
      
      if (studiedInSession > 0) {
        supabase.from("flashcard_study_sessions").insert({
          user_id: userId,
          deck_id: selectedDeckId,
          studied_count: studiedInSession,
          due_count: studiedInSession + remainingCount,
        }).then(() => {
          console.log("[Flashcards] Study session recorded:", studiedInSession, "cards");
        });
      }
    }
    
    set({ isStudyMode: false, dueCards: [], studyIndex: 0 });
    
    // Refresh cards and stats after study session
    if (selectedDeckId) {
      fetchCardsForDeck(selectedDeckId).then((cards) => {
        set({ cards, stats: computeStudyStats(cards) });
      });
    }
  },

  rateCard: async (rating: ReviewRating) => {
    const { dueCards, studyIndex } = get();
    const currentCard = dueCards[studyIndex];
    
    if (!currentCard) return;

    await reviewCard(currentCard.id, rating);

    // Remove the reviewed card from dueCards
    const updatedDueCards = dueCards.filter((_, i) => i !== studyIndex);
    
    // Adjust index if needed
    const newIndex = studyIndex >= updatedDueCards.length 
      ? Math.max(0, updatedDueCards.length - 1) 
      : studyIndex;

    set({
      dueCards: updatedDueCards,
      studyIndex: newIndex,
    });
  },

  nextCard: () => {
    const { studyIndex, dueCards } = get();
    if (studyIndex < dueCards.length - 1) {
      set({ studyIndex: studyIndex + 1 });
    }
  },

  previousCard: () => {
    const { studyIndex } = get();
    if (studyIndex > 0) {
      set({ studyIndex: studyIndex - 1 });
    }
  },

  subscribeToRealtimeUpdates: (deckId: string) => {
    // Unsubscribe from any existing channel first
    const existingChannel = get().realtimeChannel;
    if (existingChannel) {
      supabase.removeChannel(existingChannel);
    }

    const channel = supabase
      .channel(`flashcards-sync-${deckId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'flashcards',
          filter: `deck_id=eq.${deckId}`,
        },
        async (payload) => {
          console.log('[Flashcards Realtime] Update received:', payload.eventType);
          
          const { isStudyMode, selectedDeckId } = get();
          
          // If we're in study mode and got an update from another device
          if (isStudyMode && payload.eventType === 'UPDATE') {
            const updatedCard = payload.new as Flashcard;
            
            // Update the dueCards list if this card is in it
            const dueCards = get().dueCards.map((card) =>
              card.id === updatedCard.id ? { ...card, ...updatedCard } : card
            );
            
            // Remove card from dueCards if it's no longer due
            const now = new Date();
            const filteredDueCards = dueCards.filter((card) => {
              if (card.id === updatedCard.id && updatedCard.due_at) {
                return new Date(updatedCard.due_at) <= now;
              }
              return true;
            });
            
            set({ dueCards: filteredDueCards });
          }
          
          // Refresh cards list if we're viewing the deck
          if (selectedDeckId === deckId && !isStudyMode) {
            const cards = await fetchCardsForDeck(deckId);
            set({ cards, stats: computeStudyStats(cards) });
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
  },

  unsubscribeFromRealtime: () => {
    const channel = get().realtimeChannel;
    if (channel) {
      supabase.removeChannel(channel);
      set({ realtimeChannel: null });
    }
  },
}));
